import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Skeleton,
  IconButton,
} from "@mui/material";
import Header from "../../component/users/Header";
import Footer from "../../component/users/Footer";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ToastContainer, toast } from "react-toastify";
import moment from "moment";
import {
  getFinalNightDetail,
  voteFinalNightContestant,
} from "../../api/competitionFinalNight";
import FinalNightLeaderboard from "../../component/users/competitions/FinalNightLeaderboard";
import FinalNightLiveStream from "../../component/users/competitions/FinalNightLiveStream";
import JudgeScoringPanel from "../../component/users/competitions/JudgeScoringPanel";
import CompetitionBuyVotes from "../../component/users/competitions/CompetitionBuyVotes";
import CompetitionBuyVotesTrigger from "../../component/users/competitions/CompetitionBuyVotesTrigger";
import GuestUserRistModal from "../../component/Modals/user/guestUserModalRist/Modal";
import { guestVotingDisabled, guestVoteLimitHit } from "../../utils/guestVote";
import {
  isPurchaseRequiredMessage,
  shouldShowBuyVotesAnywhere,
} from "../../utils/competitionVotePurchase";

const gold = "#C9A66B";
const red = "#d93349";

export default function CompetitionFinalNight() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = localStorage.getItem("mynetgoals.com-token");
  const loggedUser = useSelector((state) => state.loggedUser.user);

  const applyWalletVotes = useCallback(
    (count) => {
      const next = Number(count);
      if (!Number.isFinite(next)) return;
      setRemainingVotes(next);
      if (!loggedUser?.device_id) {
        dispatch({ type: "SET_VOTE_COUNT", payload: next });
      }
    },
    [dispatch, loggedUser?.device_id]
  );
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [votingId, setVotingId] = useState(null);
  const [votedContestantId, setVotedContestantId] = useState(null);
  const [buyVotesOpen, setBuyVotesOpen] = useState(false);
  const [guestModal, setGuestModal] = useState(false);
  const [remainingVotes, setRemainingVotes] = useState(0);
  const [dailyVotesUsedInRound, setDailyVotesUsedInRound] = useState(0);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!token) return;
      if (!silent) setLoading(true);
      try {
        const res = await getFinalNightDetail(id, token);
        if (res.data.success) {
          const payload = res.data.data;
          setData(payload);
          if (payload?.remainingVotes != null) {
            applyWalletVotes(payload.remainingVotes);
          }
          if (payload?.dailyVotesUsedInRound != null) {
            setDailyVotesUsedInRound(Number(payload.dailyVotesUsedInRound) || 0);
          }
        } else if (!silent) toast.error(res.data.message);
      } catch (e) {
        if (!silent) {
          toast.error(e?.response?.data?.message || "Failed to load Final Night");
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id, token, applyWalletVotes]
  );

  useEffect(() => {
    load();
    const t = setInterval(() => load({ silent: true }), 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    setVotedContestantId(null);
  }, [data?.activeRound?.id]);

  const comp = data?.competition;
  const isLive = comp?.final_night_status === "live";
  const activeRound = data?.activeRound;
  const leaderboard = data?.leaderboard || [];
  const isJudge = data?.isJudge;
  const youtubeUrl = comp?.final_night_youtube_url;

  const maxFreeVotesPerRound = data?.maxFreeVotesPerRound ?? 1;
  const votePurchase = data?.vote_purchase;
  const showBuyVotesPrompt = shouldShowBuyVotesAnywhere({
    votePurchase,
    remainingVotes,
    maxFreeVotesPerDay: maxFreeVotesPerRound,
    dailyVotesUsed: dailyVotesUsedInRound,
  });

  const openBuyVotes = () => {
    if (loggedUser?.device_id) {
      setGuestModal(true);
      return;
    }
    setBuyVotesOpen(true);
  };

  const handleVote = async (contestantId) => {
    if (!activeRound) return;
    setVotingId(contestantId);
    try {
      const body = {
        competition_id: Number(id),
        round_id: activeRound.id,
        contestant_id: contestantId,
      };
      if (loggedUser?.device_id) {
        body.guest_user_id = loggedUser.id;
      }
      const res = await voteFinalNightContestant(body, token, {
        isGuest: Boolean(loggedUser?.device_id),
      });
      if (res.data.success) {
        toast.success(res.data.message || "Vote recorded!");
        setVotedContestantId(contestantId);
        if (res.data.data?.remainingVotes != null) {
          applyWalletVotes(res.data.data.remainingVotes);
        }
        if (res.data.data?.dailyVotesUsedInRound != null) {
          setDailyVotesUsedInRound(
            Number(res.data.data.dailyVotesUsedInRound) || 0
          );
        }
        load({ silent: true });
      } else {
        const payload = res.data;
        if (guestVotingDisabled(payload)) {
          toast.error(payload?.message || "Guest voting is currently disabled.");
        } else if (guestVoteLimitHit(payload) || payload?.requiresRegistration) {
          setGuestModal(true);
        }
        if (payload?.requiresPurchase || isPurchaseRequiredMessage(payload?.message)) {
          openBuyVotes();
        }
        toast.error(payload?.message || "Vote failed");
      }
    } catch (e) {
      const payload = e?.response?.data;
      if (guestVotingDisabled(payload)) {
        toast.error(payload?.message || "Guest voting is currently disabled.");
      } else if (guestVoteLimitHit(payload) || payload?.requiresRegistration) {
        setGuestModal(true);
      }
      if (
        payload?.requiresPurchase ||
        isPurchaseRequiredMessage(payload?.message)
      ) {
        openBuyVotes();
      }
      toast.error(payload?.message || "Vote failed");
    } finally {
      setVotingId(null);
    }
  };

  return (
    <>
      <ToastContainer />
      <GuestUserRistModal open={guestModal} setOpen={setGuestModal} />
      <CompetitionBuyVotes
        open={buyVotesOpen}
        onClose={() => setBuyVotesOpen(false)}
        competitionId={Number(id)}
        competitionName={comp?.name}
        votePurchase={votePurchase}
        token={token}
      />
      <Header />
      <Box className="userLayout competition-final-night-page">
        <Container maxWidth="lg" disableGutters sx={{ px: { xs: 1.5, md: 2 }, overflowX: "hidden", width: "100%" }}>
          <Box className="fn-hero">
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <IconButton onClick={() => navigate(`/competitions/${id}`)} sx={{ color: "#fff" }}>
                <KeyboardBackspaceIcon />
              </IconButton>
              {isLive ? (
                <Chip
                  icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                  label="FINAL NIGHT LIVE"
                  size="small"
                  sx={{
                    bgcolor: red,
                    color: "#fff",
                    fontWeight: 800,
                    animation: "fnPulse 2s ease-in-out infinite",
                  }}
                />
              ) : (
                <Chip label="Final Night" size="small" sx={{ bgcolor: gold, color: "#1a1d27", fontWeight: 700 }} />
              )}
            </Stack>
            {loading ? (
              <Skeleton variant="rounded" height={120} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
            ) : (
              <>
                <Typography className="fn-hero-title">{comp?.name}</Typography>
                <Typography className="fn-hero-sub">
                  {activeRound
                    ? `Now showing: ${activeRound.name}`
                    : isLive
                      ? "Awaiting next round"
                      : "Final Night has not started yet"}
                </Typography>
                {comp?.final_night_start ? (
                  <Typography className="fn-hero-date">
                    {moment(comp.final_night_start).format("ddd D MMM · HH:mm")}
                    {comp.final_night_end
                      ? ` — ${moment(comp.final_night_end).format("ddd D MMM · HH:mm")}`
                      : ""}
                  </Typography>
                ) : null}
                {showBuyVotesPrompt ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    gap={1.5}
                    sx={{ mt: 2 }}
                  >
                    <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
                      You have already used your free vote for this round.
                    </Typography>
                    <CompetitionBuyVotesTrigger
                      enabled={Boolean(votePurchase?.enabled)}
                      onClick={openBuyVotes}
                      variant="toolbar"
                    />
                  </Stack>
                ) : null}
              </>
            )}
          </Box>

          {!loading && youtubeUrl && isLive ? (
            <FinalNightLiveStream youtubeUrl={youtubeUrl} />
          ) : null}

          {!loading && data?.rounds?.length ? (
            <Box className="fn-rounds-strip">
              {data.rounds.map((r) => (
                <Box
                  key={r.id}
                  className={`fn-round-pill ${r.is_enabled ? "active" : ""} ${r.round_status === "completed" ? "done" : ""}`}
                >
                  <Typography className="fn-round-num">R{r.round_order}</Typography>
                  <Typography className="fn-round-name">{r.name}</Typography>
                </Box>
              ))}
            </Box>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: isJudge ? "1fr 380px" : "1fr" },
              gap: 3,
              pb: 6,
            }}
          >
            <Box>
              {loading ? (
                <Skeleton variant="rounded" height={400} />
              ) : leaderboard.length > 0 ? (
                <FinalNightLeaderboard
                  rows={leaderboard}
                  showVote={isLive && activeRound && !isJudge}
                  votingId={votingId}
                  votedContestantId={votedContestantId}
                  onVote={handleVote}
                  roundOrder={activeRound?.round_order}
                  judgesPercent={comp?.judges_percent}
                  usersPercent={comp?.users_percent}
                />
              ) : (
                <Box className="fn-empty">
                  <AutoAwesomeIcon sx={{ fontSize: 48, color: gold, mb: 1 }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                    {isLive
                      ? "Scores will appear when the admin enables a round."
                      : "Final Night will begin after the competition ends."}
                  </Typography>
                </Box>
              )}
            </Box>

            {isJudge && activeRound && isLive ? (
              <Box
                sx={{
                  alignSelf: "start",
                  position: { lg: "sticky" },
                  top: { lg: 24 },
                  width: "100%",
                }}
              >
                <JudgeScoringPanel
                  competitionId={Number(id)}
                  roundId={activeRound.id}
                  roundName={activeRound.name}
                  contestants={data.leaderboard}
                  token={token}
                  onSubmitted={() => load({ silent: true })}
                />
              </Box>
            ) : null}
          </Box>
        </Container>
        <Footer />
      </Box>
    </>
  );
}
